import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  LocationTrackingSettings,
  TrackingComment,
  TrackingDetails,
  TrackingPlace,
  TrackingPoint,
  TrackingSessionState,
  TrackingStay,
  TrackingTimelineItem,
  TrackingTimelineType,
  TrackingUploadResult,
  TrackingVisitStatus,
} from '../types/locationTracking';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

const TIMELINE_TYPES: TrackingTimelineType[] = [
  'CHECK_IN',
  'STAY',
  'COMMENT',
  'SIGNAL_GAP',
  'CHECK_OUT',
];
const VISIT_STATUSES: TrackingVisitStatus[] = [
  'ACTIVE',
  'COMPLETED',
  'EXPIRED',
  'STARTING',
  'NOT_RECORDED',
];

function record(value: unknown): ObjectValue {
  return value && typeof value === 'object' ? (value as ObjectValue) : {};
}

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function positive(value: unknown, fallback: number): number {
  const parsed = numberOrNull(value);
  return parsed !== null && parsed > 0 ? parsed : fallback;
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function list(value: unknown): ObjectValue[] {
  return Array.isArray(value) ? value.map(record) : [];
}

/**
 * Settings always come from the server. The fallbacks only keep the tracker
 * safe if a field is missing from an older API response.
 */
export function normalizeTrackingSettings(value: unknown): LocationTrackingSettings {
  const row = record(value);
  const sampleIntervalSeconds = positive(row.sampleIntervalSeconds, 60);
  return {
    enabled: row.enabled !== false,
    trackedSources: Array.isArray(row.trackedSources)
      ? row.trackedSources
          .map(code => String(code).trim().toUpperCase())
          .filter(Boolean)
      : [],
    sampleIntervalSeconds,
    minSampleIntervalSeconds: Math.min(
      positive(row.minSampleIntervalSeconds, sampleIntervalSeconds),
      sampleIntervalSeconds,
    ),
    distanceFilterMeters: positive(row.distanceFilterMeters, 50),
    maxAccuracyMeters: positive(row.maxAccuracyMeters, 150),
    highAccuracy: row.highAccuracy !== false,
    uploadBatchSize: positive(row.uploadBatchSize, 50),
    uploadIntervalSeconds: positive(row.uploadIntervalSeconds, 120),
    maxQueuedPoints: positive(row.maxQueuedPoints, 5000),
    liveRefreshSeconds: positive(row.liveRefreshSeconds, 60),
    longStayRadiusMeters: positive(row.longStayRadiusMeters, 150),
    longStayMinMinutes: positive(row.longStayMinMinutes, 20),
  };
}

export function normalizeTrackingState(value: unknown): TrackingSessionState {
  const row = record(value);
  const session = row.session ? record(row.session) : null;
  return {
    eligible: row.eligible === true,
    active: row.active === true,
    attendanceId: numberOrNull(row.attendanceId) ?? 0,
    attendanceSource: textOrNull(row.attendanceSource),
    session: session
      ? {
          visitId: String(session.visitId ?? ''),
          status: String(session.status ?? ''),
          startedAt: textOrNull(session.startedAt),
          endedAt: textOrNull(session.endedAt),
          pointCount: numberOrNull(session.pointCount) ?? 0,
          lastPointAt: textOrNull(session.lastPointAt),
        }
      : null,
    settings: normalizeTrackingSettings(row.settings),
  };
}

function place(value: unknown): TrackingPlace | null {
  if (!value) return null;
  const row = record(value);
  const at = textOrNull(row.at);
  if (!at) return null;
  return {
    at,
    latitude: numberOrNull(row.latitude),
    longitude: numberOrNull(row.longitude),
    accuracyMeters: numberOrNull(row.accuracyMeters),
    address: textOrNull(row.address),
  };
}

function stay(row: ObjectValue): TrackingStay | null {
  const latitude = numberOrNull(row.latitude);
  const longitude = numberOrNull(row.longitude);
  const arrivedAt = textOrNull(row.arrivedAt);
  if (latitude === null || longitude === null || !arrivedAt) return null;
  return {
    stayNumber: numberOrNull(row.stayNumber) ?? 0,
    latitude,
    longitude,
    arrivedAt,
    departedAt: textOrNull(row.departedAt),
    lastSeenAt: textOrNull(row.lastSeenAt) ?? arrivedAt,
    durationMinutes: numberOrNull(row.durationMinutes) ?? 0,
    pointCount: numberOrNull(row.pointCount) ?? 0,
    ongoing: row.ongoing === true,
    includesCheckIn: row.includesCheckIn === true,
    includesCheckOut: row.includesCheckOut === true,
    address: textOrNull(row.address),
  };
}

function comment(row: ObjectValue): TrackingComment | null {
  const commentId = numberOrNull(row.commentId);
  const createdAt = textOrNull(row.createdAt);
  if (commentId === null || !createdAt) return null;
  return {
    commentId,
    comment: typeof row.comment === 'string' ? row.comment : '',
    latitude: numberOrNull(row.latitude),
    longitude: numberOrNull(row.longitude),
    locationCapturedAt: textOrNull(row.locationCapturedAt),
    createdAt,
    address: textOrNull(row.address),
  };
}

function timelineItem(row: ObjectValue): TrackingTimelineItem | null {
  const type = row.type as TrackingTimelineType;
  const at = textOrNull(row.at);
  if (!TIMELINE_TYPES.includes(type) || !at) return null;
  return {
    id: String(row.id ?? `${type}-${at}`),
    type,
    at,
    until: textOrNull(row.until),
    latitude: numberOrNull(row.latitude),
    longitude: numberOrNull(row.longitude),
    address: textOrNull(row.address),
    durationMinutes: numberOrNull(row.durationMinutes),
    ongoing: row.ongoing === true,
    stayNumber: numberOrNull(row.stayNumber),
    comment: typeof row.comment === 'string' ? row.comment : null,
  };
}

function notNull<T>(value: T | null): value is T {
  return value !== null;
}

export function normalizeTrackingDetails(value: unknown): TrackingDetails {
  const row = record(value);
  const employee = record(row.employee);
  const visit = record(row.visit);
  const summary = record(row.summary);
  const settings = record(row.settings);
  const map = record(row.map);
  const status = String(visit.status ?? '') as TrackingVisitStatus;
  return {
    eligible: row.eligible === true,
    active: row.active === true,
    trackingAvailable: row.trackingAvailable !== false,
    employee: {
      employeeId: numberOrNull(employee.employeeId) ?? 0,
      displayName: textOrNull(employee.displayName),
      employeeNumber: textOrNull(employee.employeeNumber),
    },
    visit: {
      visitId: textOrNull(visit.visitId),
      attendanceId: numberOrNull(visit.attendanceId) ?? 0,
      attendanceDate: textOrNull(visit.attendanceDate),
      attendanceSource: textOrNull(visit.attendanceSource),
      status: VISIT_STATUSES.includes(status) ? status : 'NOT_RECORDED',
      startedAt: textOrNull(visit.startedAt),
      endedAt: textOrNull(visit.endedAt),
    },
    checkIn: place(row.checkIn),
    checkOut: place(row.checkOut),
    currentLocation: row.active === true ? place(row.currentLocation) : null,
    summary: {
      durationMinutes: numberOrNull(summary.durationMinutes),
      distanceMeters: numberOrNull(summary.distanceMeters) ?? 0,
      pointCount: numberOrNull(summary.pointCount) ?? 0,
      stayCount: numberOrNull(summary.stayCount) ?? 0,
      commentCount: numberOrNull(summary.commentCount) ?? 0,
      lastLocationAt: textOrNull(summary.lastLocationAt),
    },
    stays: list(row.stays).map(stay).filter(notNull),
    comments: list(row.comments).map(comment).filter(notNull),
    route: list(row.route)
      .map(point => ({
        latitude: numberOrNull(point.latitude),
        longitude: numberOrNull(point.longitude),
        capturedAt: textOrNull(point.capturedAt) ?? '',
      }))
      .filter(
        (point): point is { latitude: number; longitude: number; capturedAt: string } =>
          point.latitude !== null && point.longitude !== null,
      ),
    timeline: list(row.timeline).map(timelineItem).filter(notNull),
    settings: {
      longStayRadiusMeters: positive(settings.longStayRadiusMeters, 150),
      longStayMinMinutes: positive(settings.longStayMinMinutes, 20),
      liveRefreshSeconds: positive(settings.liveRefreshSeconds, 60),
    },
    map: {
      tileUrl: textOrNull(map.tileUrl) ?? '',
      attribution: textOrNull(map.attribution) ?? '',
    },
    generatedAt: textOrNull(row.generatedAt),
  };
}

export async function getLocationTrackingSettings(): Promise<LocationTrackingSettings> {
  const response = await apiClient.get<Envelope<unknown>>(
    API_ENDPOINTS.ATTENDANCE.TRACKING.SETTINGS,
  );
  return normalizeTrackingSettings(response.data.data);
}

/** Idempotent: the server keeps one session per check-in. */
export async function startLocationTracking(
  attendanceId: number,
  startFix?: { latitude: number; longitude: number; accuracyMeters: number | null } | null,
): Promise<TrackingSessionState> {
  const response = await apiClient.post<Envelope<unknown>>(
    API_ENDPOINTS.ATTENDANCE.TRACKING.START(attendanceId),
    startFix ?? {},
  );
  return normalizeTrackingState(response.data.data);
}

export async function uploadTrackingPoints(
  attendanceId: number,
  points: TrackingPoint[],
): Promise<TrackingUploadResult> {
  const response = await apiClient.post<Envelope<unknown>>(
    API_ENDPOINTS.ATTENDANCE.TRACKING.POINTS(attendanceId),
    { points },
  );
  const row = record(response.data.data);
  return {
    accepted: numberOrNull(row.accepted) ?? 0,
    duplicates: numberOrNull(row.duplicates) ?? 0,
    ignored: numberOrNull(row.ignored) ?? 0,
    rejected: numberOrNull(row.rejected) ?? 0,
    active: row.active === true,
  };
}

export async function getTrackingDetails(
  attendanceId: number,
): Promise<TrackingDetails> {
  const response = await apiClient.get<Envelope<unknown>>(
    API_ENDPOINTS.ATTENDANCE.TRACKING.DETAILS(attendanceId),
  );
  return normalizeTrackingDetails(response.data.data);
}
